import React from "react";
import { YalolaLogo } from "@/components/YalolaLogo";

// Single shimmering surface. The animated sweep comes from `.yalola-skeleton` in index.css.
const Sk = ({ className = "", style }) => (
  <div className={`yalola-skeleton ${className}`} style={style} />
);
const Bar = ({ w = "100%", h = 12, r = 6 }) => <Sk style={{ width: w, height: h, borderRadius: r }} />;
const Circle = ({ s = 44 }) => <Sk style={{ width: s, height: s, borderRadius: "50%", flexShrink: 0 }} />;

export const PostSkeleton = () => (
  <div className="yalola-card p-4" data-testid="post-skeleton">
    <div className="flex items-center gap-3 mb-4">
      <Circle s={44} />
      <div className="flex-1 space-y-2">
        <Bar w="42%" />
        <Bar w="26%" h={10} />
      </div>
    </div>
    <div className="space-y-2 mb-4">
      <Bar />
      <Bar w="92%" />
      <Bar w="68%" />
    </div>
    <Sk style={{ width: "100%", height: 200, borderRadius: 14 }} />
    <div className="flex gap-6 mt-4">
      <Bar w="72px" h={16} />
      <Bar w="72px" h={16} />
      <Bar w="72px" h={16} />
    </div>
  </div>
);

export const FeedSkeleton = ({ count = 3 }) => (
  <div className="space-y-4" data-testid="feed-skeleton">
    {Array.from({ length: count }).map((_, i) => <PostSkeleton key={i} />)}
  </div>
);

// Full app-shell mock shown while the app boots (logo top bar + story bar + feed),
// so first paint feels like Facebook/YouTube instead of a bare "Chargement..." line.
export const AppBootSkeleton = () => (
  <div className="min-h-screen" style={{ background: "var(--yalola-bg)" }} data-testid="app-boot-skeleton">
    <header className="sticky top-0 z-40 yalola-glass" style={{ paddingTop: "var(--sa-top)" }}>
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 h-14 md:h-16 flex items-center gap-3">
        <YalolaLogo size={34} />
        <div className="hidden sm:block"><Bar w="90px" h={18} /></div>
        <div className="flex-1" />
        <div className="flex items-center gap-2"><Circle s={36} /><Circle s={36} /></div>
      </div>
    </header>
    <main className="max-w-[680px] mx-auto px-3 sm:px-6 py-4 md:py-5">
      <div className="flex gap-3 mb-4 overflow-hidden" data-testid="app-boot-stories">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-1.5 shrink-0">
            <Circle s={64} />
            <Bar w="48px" h={8} />
          </div>
        ))}
      </div>
      <FeedSkeleton count={3} />
    </main>
  </div>
);

// Profile page body (renders inside <AppShell>): cover + avatar + name + tabs + intro/posts.
export const ProfileSkeleton = () => (
  <div className="max-w-[1000px] mx-auto space-y-4" data-testid="profile-skeleton">
    <div className="yalola-card overflow-hidden">
      <Sk className="h-40 md:h-64" style={{ borderRadius: 0 }} />
      <div className="px-5 md:px-6 pb-4">
        <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-14 md:-mt-16">
          <div className="shrink-0 w-28 md:w-36">
            <Sk className="h-28 w-28 md:h-36 md:w-36 rounded-full border-4 border-white" style={{ borderRadius: "50%" }} />
          </div>
          <div className="flex-1 min-w-0 md:pb-2 space-y-2">
            <Bar w="45%" h={24} />
            <Bar w="30%" h={12} />
          </div>
          <div className="flex gap-2 md:pb-2">
            <Sk style={{ width: 130, height: 38, borderRadius: 9999 }} />
            <Sk style={{ width: 110, height: 38, borderRadius: 9999 }} />
          </div>
        </div>
        <div className="flex items-center gap-8 mt-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-1.5"><Bar w="36px" h={16} /><Bar w="56px" h={10} /></div>
          ))}
        </div>
      </div>
      <div className="flex gap-6 border-t px-4 py-3.5" style={{ borderColor: "var(--yalola-border)" }}>
        {Array.from({ length: 4 }).map((_, i) => <Bar key={i} w="76px" h={14} />)}
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <div className="lg:col-span-5">
        <div className="yalola-card p-5 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => <Bar key={i} w={`${92 - i * 12}%`} />)}
        </div>
      </div>
      <div className="lg:col-span-7 space-y-4"><PostSkeleton /><PostSkeleton /></div>
    </div>
  </div>
);

// Detail page body (church / group / live): cover + logo + heading + a content card.
export const DetailSkeleton = () => (
  <div className="max-w-[900px] mx-auto space-y-4" data-testid="detail-skeleton">
    <div className="yalola-card overflow-hidden">
      <Sk className="h-40 md:h-52" style={{ borderRadius: 0 }} />
      <div className="px-4 md:px-6 pb-4 -mt-12">
        <Sk className="h-24 w-24 border-4 border-white" style={{ borderRadius: 16 }} />
        <div className="mt-3 space-y-2.5">
          <Bar w="52%" h={26} />
          <Bar w="30%" h={12} />
          <Bar w="70%" h={12} />
        </div>
      </div>
    </div>
    <div className="yalola-card p-5 space-y-3">
      {Array.from({ length: 3 }).map((_, i) => <Bar key={i} w={`${90 - i * 16}%`} />)}
    </div>
  </div>
);

// Generic vertical list of simple cards (prayers, saved, history, reading plans...).
export const ListSkeleton = ({ count = 4 }) => (
  <div className="space-y-4" data-testid="list-skeleton">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="yalola-card p-4 flex items-center gap-3">
        <Circle s={44} />
        <div className="flex-1 space-y-2">
          <Bar w="45%" />
          <Bar w="82%" h={10} />
          <Bar w="60%" h={10} />
        </div>
      </div>
    ))}
  </div>
);

// Grid of media cards (churches, groups, events, marketplace, matching...).
export const CardGridSkeleton = ({ count = 6, cols = "sm:grid-cols-2 xl:grid-cols-3" }) => (
  <div className={`grid grid-cols-1 ${cols} gap-4`} data-testid="card-grid-skeleton">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="yalola-card overflow-hidden">
        <Sk style={{ height: 128, borderRadius: 0 }} />
        <div className="p-4 space-y-2">
          <Bar w="72%" />
          <Bar w="90%" h={10} />
          <Bar w="42%" h={10} />
        </div>
      </div>
    ))}
  </div>
);

// Avatar rows (messages inbox / conversation lists).
export const RowsSkeleton = ({ count = 6 }) => (
  <div data-testid="rows-skeleton">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: "var(--yalola-border)" }}>
        <Circle s={48} />
        <div className="flex-1 space-y-2">
          <Bar w="38%" />
          <Bar w="70%" h={10} />
        </div>
      </div>
    ))}
  </div>
);

// Simple admin skeleton (stat cards + a content card).
export const AdminSkeleton = () => (
  <div className="space-y-4" data-testid="admin-skeleton">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="yalola-card p-5 space-y-2.5"><Bar w="55%" h={10} /><Bar w="42%" h={26} /></div>
      ))}
    </div>
    <div className="yalola-card p-5 space-y-3">
      {Array.from({ length: 5 }).map((_, i) => <Bar key={i} w={`${92 - i * 10}%`} />)}
    </div>
  </div>
);
